<template>
    <!-- sl-orc stands for "SwissLens - Over-Refraction Calculator" -->
    <div class="sl-widget sl-calculator sl-orc" @keydown.esc="emit('close')">
        <div class="sl-widget__header">
            <div class="sl-widget__title">
                <span>{{ t('calculators.over_refraction.title') }}</span>

                <mdi-icon
                    v-tippy
                    :content="t('common.close')"
                    icon="mdiClose"
                    class="cursor-pointer text-gray-500 hover:text-primary-500 transition"
                    @click="emit('close')"
                />
            </div>
        </div>

        <div class="sl-widget__body">
            <!-- ---------------------------------------- Side Selector -->
            <radio-button v-model="side" :items="sideOptions" />

            <div class="grid grid-cols-4 gap-2 justify-center items-center text-center">
                <!-- ---------------------------------------- Calculator Header -->

                <div></div>

                <div class="transform -rotate-45 my-3">
                    {{ t('calculators.common.contact_lens') }}
                </div>

                <div class="transform -rotate-45 my-3">
                    {{ t('calculators.over_refraction.over_refraction') }}
                </div>

                <div class="transform -rotate-45 my-3">
                    {{ t('calculators.common.result') }}
                </div>

                <!-- ---------------------------------------- Calculator Line 1: Sphere -->

                <div>
                    {{ t('parameters.LENS_SPHERE.label') }}
                </div>

                <div>
                    <lsn-input
                        v-model="lens.sphere"
                        :tabindex="1001"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        ref="autofocus"
                        v-model="overRefraction.sphere"
                        :tabindex="1002"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        :model-value="result.sphere"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        disabled
                    />
                </div>

                <!-- ---------------------------------------- Calculator Line 2: Cylinder -->

                <div>
                    {{ t('parameters.LENS_CYLINDER.label') }}
                </div>

                <div>
                    <lsn-input
                        v-model="lens.cylinder"
                        :tabindex="1001"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        v-model="overRefraction.cylinder"
                        :tabindex="1002"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        v-model="result.cylinder"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        disabled
                    />
                </div>

                <!-- ---------------------------------------- Calculator Line 3: Axis -->

                <div>
                    {{ t('parameters.LENS_CYLINDER_AXIS.label') }}
                </div>

                <div>
                    <lsn-input
                        v-model="lens.axis"
                        :tabindex="1001"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        v-model="overRefraction.axis"
                        :tabindex="1002"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        v-model="result.axis"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        disabled
                    />
                </div>

                <!-- ---------------------------------------- Calculator Line 4: Stabilization (TABO) -->

                <div>
                    {{ t('calculators.over_refraction.stabil_tabo') }}
                </div>

                <div></div>

                <div>
                    <lsn-input
                        v-model="overRefraction.stabilization"
                        :tabindex="1002"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div></div>

                <!-- ---------------------------------------- Calculator Line 5: Addition -->

                <div>
                    {{ t('parameters.LENS_ADDITION.label') }}
                </div>

                <div>
                    <lsn-input
                        v-model="lens.addition"
                        :tabindex="1001"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        v-model="overRefraction.addition"
                        :tabindex="1002"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div>
                    <lsn-input
                        v-model="result.addition"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        disabled
                    />
                </div>

                <!-- ---------------------------------------- Calculator Line 6: Vertex Power -->

                <div>
                    {{ t('calculators.common.vertex_power') }}
                </div>

                <div></div>

                <div>
                    <lsn-input
                        v-model="overRefraction.vertexPower"
                        :tabindex="1002"
                        placeholder="0.00"
                        class="lsn-form__wrapper--padless sl-orc__input"
                        @update:model-value="calculate"
                    />
                </div>

                <div></div>
            </div>

            <!-- Incompatible Lens Notice -->
            <div v-if="cForbidden" class="mt-4 rounded p-2 bg-yellow-100 text-yellow-700">
                <div class="flex items-center gap-2 mb-1 font-bold">
                    <mdi-icon icon="mdiAlert" class="w-4 h-4" />

                    <span class="text-yellow-800">
                        {{ cFitting.formPrototype.label }}
                    </span>
                </div>

                <div class="ml-6">
                    {{ t('calculators.over_refraction.incompatibility_notice') }}
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="sl-widget__actions">
            <div
                class="sl-widget__button sl-widget__button--white"
                :tabindex="1003"
                @click="emit('close')"
                @keypress.enter="emit('close')"
            >
                {{ t('common.cancel') }}
            </div>

            <div
                class="sl-widget__button"
                :class="attributeButtonClass"
                :tabindex="1004"
                @click="attribute"
                @keypress.enter="attribute"
            >
                {{ t(`calculators.common.attribute_${side}`) }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import {
    PROTOTYPES_WITHOUT_OVER_REFRACTION_CALCULATOR,
} from '@/views/order/lenses/LensOrder.vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Fitting from '@/models/Fitting';
import RadioButton from '@/components/RadioButton.vue';

// ------------------------------------------------------------ CONSTANTS


// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const { t } = useI18n({ useScope: 'global' });

// ------------------------------------------------------------ EMITS

const emit = defineEmits('close');

// ------------------------------------------------------------ TEMPLATE REFS

const autofocus = useTemplateRef('autofocus');

// ------------------------------------------------------------ DATA

const side = ref('');
const attributing = ref(false);
const lens = ref({
    sphere:   0.00,
    cylinder: 0.00,
    axis:     0.00,
    addition: 0.00,
});
const overRefraction = ref({
    sphere:        0.00,
    cylinder:      0.00,
    axis:          0.00,
    stabilization: 0.00,
    addition:      0.00,
    vertexPower:   14.00,
});
const result = ref({
    sphere:   0.00,
    cylinder: 0.00,
    axis:     0.00,
    addition: 0.00,
});

// ------------------------------------------------------------ COMPUTED

const sideOptions = computed(() => ['right', 'left'].map(side => ({
    ovalue:     side,
    label:      t(`common.${side}-lens`),
    is_enabled: side === 'right'
        ? rightFitting.value && !rightIsForbidden.value
        : leftFitting.value && !leftIsForbidden.value,
})));

/** @type {import("vue").ComputedRef<Fitting>} */
const rightFitting = computed(() =>
    store.getters['lensOrder/getFitting']('right')
);

/** @type {import("vue").ComputedRef<Fitting>} */
const leftFitting = computed(() =>
    store.getters['lensOrder/getFitting']('left')
);

const cFitting = computed(() =>
{
    if(side.value === 'left')
    {
        return leftFitting.value;
    }

    return rightFitting.value;
});

const attributeButtonClass = computed(() =>
{
    const classes = [];
    if(attributing.value || cForbidden.value)
    {
        classes.push('sl-widget__button--disabled');
    }
    else
    {
        classes.push('sl-widget__button--primary');
    }

    return classes;
});

const rightIsForbidden = computed(() =>
    PROTOTYPES_WITHOUT_OVER_REFRACTION_CALCULATOR.some(
        proto => rightFitting.value?.formPrototype.code === proto
    )
);

const leftIsForbidden = computed(() =>
    PROTOTYPES_WITHOUT_OVER_REFRACTION_CALCULATOR.some(
        proto => leftFitting.value?.formPrototype.code === proto
    )
);

const cForbidden = computed(() =>
{
    if(side.value === 'left')
    {
        return leftIsForbidden.value;
    }

    return rightIsForbidden.value;
});

// ------------------------------------------------------------ METHODS

function preSelectSide()
{
    side.value = rightFitting.value ? 'right' : 'left';
}

function loadValues()
{
    const params = cFitting.value?.formPrototype.parameters;

    lens.value.sphere   = params.get('LENS_SPHERE'       )?.value || 0.00;
    lens.value.cylinder = params.get('LENS_CYLINDER'     )?.value || 0.00;
    lens.value.axis     = params.get('LENS_CYLINDER_AXIS')?.value || 0.00;
    lens.value.addition = params.get('LENS_ADDITION'     )?.value || 0.00;
}

function calculate()
{
    // Replace commas with periods
    lens.value.sphere                  = `${lens.value.sphere}`.replace(/,/g, '.');
    lens.value.cylinder                = `${lens.value.cylinder}`.replace(/,/g, '.');
    lens.value.axis                    = `${lens.value.axis}`.replace(/,/g, '.');
    lens.value.addition                = `${lens.value.addition}`.replace(/,/g, '.');
    overRefraction.value.sphere        = `${overRefraction.value.sphere}`.replace(/,/g, '.');
    overRefraction.value.cylinder      = `${overRefraction.value.cylinder}`.replace(/,/g, '.');
    overRefraction.value.axis          = `${overRefraction.value.axis}`.replace(/,/g, '.');
    overRefraction.value.stabilization = `${overRefraction.value.stabilization}`.replace(/,/g, '.');
    overRefraction.value.addition      = `${overRefraction.value.addition}`.replace(/,/g, '.');
    overRefraction.value.vertexPower   = `${overRefraction.value.vertexPower}`.replace(/,/g, '.');

    // Force negative value for cylinder
    lens.value.cylinder           = -Math.abs(lens.value.cylinder) || 0.00;
    overRefraction.value.cylinder = -Math.abs(overRefraction.value.cylinder) || 0.00;

    // Lens values
    const sph1 = parseFloat(lens.value.sphere);
    const cyl1 = parseFloat(lens.value.cylinder);
    const axe1 = constrainTo90(parseFloat(lens.value.axis));
    const add1 = parseFloat(lens.value.addition);

    // Over-refraction values
    const sph2 = parseFloat(overRefraction.value.sphere);
    let cyl2 = parseFloat(overRefraction.value.cylinder) || 0.00001;
    const axe2 = constrainTo90(parseFloat(overRefraction.value.axis));
    const add2 = parseFloat(overRefraction.value.addition);
    const stab = constrainTo90(parseFloat(overRefraction.value.stabilization));

    const dvo = parseFloat(overRefraction.value.vertexPower) || 0.00;
    const vertexD = parseInt(dvo) / 1000;

    // Calculation variables
    let resSph;
    let resCyl;
    let resAxe;

    if(sph2 === 0 && cyl2 === 0)
    {
        resSph = sph1;
        resCyl = cyl1;
        resAxe = (axe1 - stab) % 180;
        if(resAxe < 0)
            resAxe += 180;
    }
    else
    {
        const { PI, sqrt, cos, acos } = Math;

        const A = sph1 + cyl1;
        const B = -cyl1;
        const C = axe1 + 90;
        const D2 = sph2 + cyl2;
        const D = D2 / (1 - vertexD * D2);
        const Sph2_2 = sph2 / (1 - vertexD * sph2);
        cyl2 = D - Sph2_2;
        const E = -cyl2;
        const F = axe2 + 90 - stab;

        let CC = C;
        if(C - F > 90)
            CC -= 180;

        let FF = F;
        if(F - C > 90)
            FF -= 180;

        const FC = (FF - CC) * PI / 180;
        resCyl = -sqrt(B * B + E * E + 2 * B * E * cos(2 * FC));
        resSph = A + D + 0.5 * (B + E - resCyl);
        const I1 = B + E * cos(2 * FC);
        const I2 = parseFloat((-I1 / resCyl).toFixed(3));
        const I3 = 180 * acos(I2) / PI;
        const I = 0.5 * I3;

        if(FF < CC)
            resAxe = CC - I - 90;
        else
            resAxe = CC + I - 90;
    }

    if(resAxe < 0)
        resAxe += 180;

    // Affect results
    result.value.sphere   = resSph.toFixed(2);
    result.value.cylinder = resCyl.toFixed(2);
    result.value.axis     = Math.round(resAxe);
    result.value.addition = add1 + add2;
}

async function attribute()
{
    if(attributing.value)
    {
        return;
    }

    if(!['right', 'left'].includes(side.value))
    {
        throw new RangeError(`Invalid value for side: "${side.value}"`);
    }

    if(cForbidden.value)
    {
        return;
    }

    attributing.value = true;

    const fitting = cFitting.value;

    // Values will be set only if parameter exists.
    fitting.getPrototype().setValue('LENS_SPHERE',        parseFloat(result.value.sphere));
    fitting.getPrototype().setValue('LENS_CYLINDER',      parseFloat(result.value.cylinder));
    fitting.getPrototype().setValue('LENS_CYLINDER_AXIS', parseFloat(result.value.axis));
    fitting.getPrototype().setValue('LENS_ADDITION',      parseFloat(result.value.addition));

    try
    {
        await store.dispatch('lensOrder/template', { side: side.value });
        emit('close');
    }
    finally
    {
        attributing.value = false;
    }
}

function constrainTo90(n)
{
    let out = n;

    while(out > 90) out -= 180;
    while(out < -90) out += 180;

    return out;
}

// ------------------------------------------------------------ WATCHERS

watch(side, () =>
{
    loadValues();
    calculate();
});

// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(async() =>
{
    preSelectSide();

    await nextTick();
    autofocus.value?.focus();
});
</script>

<style scoped>
@reference "#main.css";

.sl-orc :deep(.sl-orc__input input)
{
    @apply text-center;
}
</style>
